import { useState, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Users, 
  Calendar, 
  PenTool, 
  Lightbulb,
  Sparkles,
  Loader2 
} from "lucide-react";
import { API } from "@/App";

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API}/admin/users`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setUsers(data.users);
          setTotal(data.total);
        } else {
          setError("Failed to load admin data");
        }
      } catch (err) {
        setError("Failed to load admin data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const formatDate = (dateStr, fallbackLabel = null) => {
    if (!dateStr) return fallbackLabel || "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getLastLoginDisplay = (user) => {
    if (user.last_login) {
      return formatDate(user.last_login);
    }
    // For users who registered before tracking was added, show their join date
    if (user.created_at) {
      return `~${formatDate(user.created_at)} (joined)`;
    }
    return "Before tracking";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-primary" />
          <h3 className="font-display text-lg uppercase tracking-wider">User Activity</h3>
        </div>
        <span className="text-sm text-muted-foreground">{total} users</span>
      </div>

      {/* User List */}
      <ScrollArea className="h-[400px]">
        <div className="space-y-3">
          {users.map((user) => (
            <div
              key={user.user_id}
              className="bg-card border border-border rounded-sm p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium">{user.name || "Unknown"}</h4>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
                {user.is_admin && (
                  <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                    Admin
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground text-xs">Last Login</p>
                    <p className="font-mono text-xs">{getLastLoginDisplay(user)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-xs">#</span>
                  <div>
                    <p className="text-muted-foreground text-xs">Logins</p>
                    <p className="font-mono">{user.login_count || 0}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground text-xs">Bits</p>
                    <p className="font-mono">{user.bits_count || 0}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground text-xs">Captures</p>
                    <p className="font-mono">{user.ideas_count || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-border flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  <span>{user.play_ideas_count || 0} play ideas</span>
                </div>
                <span>Joined: {formatDate(user.created_at)}</span>
              </div>
            </div>
          ))}
          
          {users.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No users yet</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
